import { isSignInWithEmailLink, signInWithEmailLink } from "firebase/auth";
import { LoaderCircle } from "lucide-react";
import { useEffect, useRef, useState, type FC, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";

import {
  AuthButton,
  AuthCardFooter,
  AuthField,
  AuthFooterAction,
  AuthHeader,
  AuthShell
} from "@/features/login";

import { auth } from "../../../api/firebaseApi";
import { PortalAPI } from "../api/portalApi";
import { PORTAL_EMAIL_KEY, PORTAL_PATH } from "../routes";

type Stage = "email" | "sent" | "confirm" | "finishing";

export const PortalSignInPage: FC = () => {
  const navigate = useNavigate();
  const isLink = isSignInWithEmailLink(auth, window.location.href);
  const stored = localStorage.getItem(PORTAL_EMAIL_KEY) ?? "";
  const [stage, setStage] = useState<Stage>(
    isLink ? (stored ? "finishing" : "confirm") : "email"
  );
  const [email, setEmail] = useState(stored);
  const [error, setError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const started = useRef(false);

  const finish = async (address: string) => {
    setStage("finishing");
    setError(null);
    try {
      await signInWithEmailLink(auth, address, window.location.href);
      localStorage.removeItem(PORTAL_EMAIL_KEY);
      localStorage.setItem("loginType", "company");
      navigate(PORTAL_PATH, { replace: true });
    } catch {
      setStage("confirm");
      setError(
        "That link didn't work. It may have expired or been used already. Send yourself a new one."
      );
    }
  };

  useEffect(() => {
    if (stage === "finishing" && stored && !started.current) {
      started.current = true;
      void finish(stored);
    }
  }, []);

  const send = async (e: FormEvent) => {
    e.preventDefault();
    const address = email.trim();
    if (!address || sending) return;
    setError(null);
    setSending(true);
    try {
      await PortalAPI.sendSignInLink(address);
      localStorage.setItem(PORTAL_EMAIL_KEY, address);
      setStage("sent");
    } catch {
      setError("Couldn't send the link. Try again in a minute.");
    } finally {
      setSending(false);
    }
  };

  const ssoFooter = (
    <AuthCardFooter lead="Your company uses single sign-on?">
      <AuthFooterAction to="/login">Sign In With SSO</AuthFooterAction>
    </AuthCardFooter>
  );

  const header =
    stage === "sent" ? (
      <div role="status">
        <AuthHeader
          title="Check Your Email"
          subtitle={`If ${email.trim()} is invited to an HR portal, a sign-in link is on its way. Open it on this device.`}
        />
      </div>
    ) : stage === "finishing" ? (
      <div role="status">
        <AuthHeader
          title={
            <span className="inline-flex items-center gap-3">
              <LoaderCircle
                className="size-6 animate-spin text-ink-faint"
                aria-hidden="true"
              />
              Signing You In…
            </span>
          }
        />
      </div>
    ) : (
      <AuthHeader
        title="HR Portal"
        subtitle="Your immigration firm's portal for your company's employees, requests and spend."
      />
    );

  return (
    <AuthShell legal={false}>
      <div className="flex flex-col" data-testid="portal-sign-in">
        {header}

        {stage === "sent" ? (
          <div className="mt-11 flex flex-col">
            <AuthButton
              variant="secondary"
              fullWidth
              onClick={() => setStage("email")}
            >
              Use a Different Email
            </AuthButton>
            {ssoFooter}
          </div>
        ) : stage === "finishing" ? null : (
          <form
            className="mt-11 flex flex-col"
            noValidate
            onSubmit={(e) =>
              stage === "confirm"
                ? (e.preventDefault(), void finish(email.trim()))
                : void send(e)
            }
          >
            <div className="flex flex-col gap-5">
              <AuthField
                id="portal-email"
                label={
                  stage === "confirm"
                    ? "Confirm the Email the Link Was Sent To"
                    : "Work Email"
                }
                type="email"
                autoFocus
                autoComplete="email"
                inputMode="email"
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.currentTarget.value)}
                error={error}
              />
              <AuthButton
                type="submit"
                fullWidth
                forward
                loading={sending}
                loadingText="Sending…"
              >
                {stage === "confirm" ? "Sign In" : "Email Me a Sign-In Link"}
              </AuthButton>
              {stage === "confirm" && error ? (
                <AuthButton
                  variant="secondary"
                  fullWidth
                  onClick={() => {
                    setStage("email");
                    setError(null);
                  }}
                >
                  Send a New Link
                </AuthButton>
              ) : null}
            </div>
            {ssoFooter}
          </form>
        )}
      </div>
    </AuthShell>
  );
};
